/**
 * Quoting.
 *
 * WHERE THE NUMBERS COME FROM, AND WHY IT MATTERS
 * Every premium this module returns is looked up in a rate table the agency
 * loaded from its own carrier rate cards. Nothing here estimates, interpolates
 * across ages, or infers a rate it was not given. That restraint is the whole
 * design.
 *
 * A quoted premium is a representation made to a member of the public. Quote a
 * family $47 a month on a policy that issues at $89 and the agent has not made
 * a rounding error — they have misquoted, which is an errors-and-omissions
 * claim and a market-conduct complaint. Software that produces a plausible
 * number when it does not know the real one is worse than software that admits
 * it does not know, because the agent cannot tell the two apart.
 *
 * So every function here either finds a real rate or says it has none, and the
 * screens are built to show "rates not loaded" as a normal, unembarrassing
 * state that sends the agent to the carrier's own quoter.
 */

import { annualisePremium, type PremiumMode, type ProductType } from './policies.js';

/* ------------------------------------------------------------- the person -- */

export type Sex = 'male' | 'female';

/**
 * Sex rather than gender, and only because rate tables are filed that way.
 *
 * Life insurance rates are filed by sex, so a quote cannot be looked up
 * without it. It is asked for here because the carrier's table demands it, and
 * for no other purpose — it is not used for routing, scoring or anything else.
 * A handful of states require unisex rates on some products; those cards are
 * loaded with both rows set the same and the answer comes out identical.
 */
export interface Applicant {
  age: number;
  sex: Sex;
  tobacco: boolean;
  /** Two-letter state code. Decides which products may even be offered. */
  state: string;
  /**
   * What they want covered, in cents — or null for "show me everything".
   *
   * On a call the question is usually "what can you do for them?" before it is
   * ever "quote exactly $15,000". Left null, a table-rated product returns
   * every face amount its card prints at this age, cheapest first, and the
   * agent reads down the list. A per-thousand product cannot be priced without
   * one, because its premium IS rate x face, and it says so instead.
   */
  faceCents: number | null;
}

/* ------------------------------------------------------------- the product -- */

export type UnderwritingKind = 'full' | 'accelerated' | 'simplified' | 'guaranteed';

export const UNDERWRITING_KIND_LABEL: Record<UnderwritingKind, string> = {
  full: 'Fully underwritten',
  accelerated: 'Accelerated — no exam if they qualify',
  simplified: 'Simplified issue — health questions, no exam',
  guaranteed: 'Guaranteed issue — no health questions',
};

/**
 * How a product's rate card is shaped.
 *
 * Final expense cards print a premium for each age and face amount outright.
 * Term cards give a rate per thousand of cover plus a flat policy fee, because
 * one page then covers every face amount. Both are common, so both are stored
 * as they are printed rather than converted into each other — a conversion is a
 * place for a rounding error to hide, and these are the numbers an agent says
 * out loud.
 */
export type RateMode = 'table' | 'per_thousand';

export interface CarrierProduct {
  id: string;
  carrierId: string;
  name: string;
  productType: ProductType;
  underwriting: UnderwritingKind;
  minAge: number;
  maxAge: number;
  minFaceCents: number;
  maxFaceCents: number;
  /** Term lengths offered. Empty for products that are not term. */
  termYears: number[];
  tobaccoAllowed: boolean;
  /** Empty means "anywhere the producer is licensed". */
  states: string[];
  rateMode: RateMode;
  /** A flat fee added to every policy, in cents. Usually only on term. */
  policyFeeCents: number;
  /** The carrier's own quoting tool, for when rates are not loaded. */
  quoteUrl: string | null;
  /**
   * When a person confirmed the issue limits above against the carrier's guide.
   *
   * The engine does not price differently on it — a rate is a rate. It is
   * carried here so that everything downstream of a quote can say whether the
   * limits that produced it were ever checked, because those limits decide who
   * gets offered a carrier at all.
   */
  verifiedAt: Date | null;
  archivedAt: Date | null;
}

/**
 * One row of a rate card, exactly as printed.
 *
 * `faceCents` is set on 'table' products and null on 'per_thousand' ones, where
 * the rate applies to any face amount in the product's band. `termYears` is
 * null on products that are not term.
 */
export interface RateRow {
  productId: string;
  sex: Sex;
  tobacco: boolean;
  age: number;
  termYears: number | null;
  faceCents: number | null;
  /**
   * On a 'table' product: the monthly premium for that face amount.
   * On a 'per_thousand' product: the monthly rate for each $1,000 of cover.
   * Both in cents, both as the card prints them.
   */
  rateCents: number;
  /** The class this row is for — 'standard' where a card does not say. */
  rateClass: string;
  /**
   * When the card this row was typed from takes effect, and what it was.
   *
   * Carried on the row rather than looked up later because `Priced.source` is
   * the row, and the whole promise of this engine is that a premium can be
   * traced back to the thing it came from. A premium that traces to a row which
   * traces to nobody is only half a trace — and a card typed from a 2023 sheet
   * is otherwise indistinguishable from one typed this morning.
   *
   * Both optional. An undated card is a real and ordinary state; saying so is
   * more useful than a date somebody invented to satisfy a required field.
   */
  effectiveOn?: Date | null;
  sourceNote?: string | null;
}

/* ------------------------------------------------------------ provenance -- */

/**
 * How far a premium can be traced back.
 *
 * WHY THIS IS SEPARATE FROM A PRODUCT'S `verifiedAt`
 * `verifiedAt` says somebody checked the ISSUE LIMITS — the ages and face
 * amounts that decide whether a carrier may be offered at all. It says nothing
 * whatever about the premium. The two were conflated on the quote screen, which
 * warned about unchecked limits and stayed silent about the number the agent
 * actually reads out loud.
 *
 * That silence is the dangerous half. A wrong limit produces a carrier that is
 * wrongly present or wrongly absent, which an experienced agent may well
 * notice. An unsourced premium produces a figure indistinguishable from a real
 * one, and the family finds out at issue — the exact failure this engine exists
 * to make impossible.
 *
 *   documented  the row names the document it was typed from. A human can go
 *               and check it.
 *   dated       the row records when the card takes effect but not which card.
 *               Enough to judge whether it is current, not enough to check.
 *   unsourced   neither. The premium traces to a row, and the row traces to
 *               nobody.
 *
 * Deliberately three states rather than a boolean. "Dated but unnamed" is the
 * ordinary condition of a card typed quickly from a real sheet; folding it into
 * "unsourced" would cry wolf on cards that are almost certainly fine, and after
 * that nobody reads the warning that matters.
 */
export type RateProvenance = 'documented' | 'dated' | 'unsourced';

/**
 * Takes a Date OR the string a Date becomes once it has crossed the wire.
 *
 * The browser is the caller that matters here — it reads these rows out of a
 * JSON response, where `effectiveOn` arrives as "2026-04-01", not as a Date.
 * A signature that accepted only Date would compile, run, and classify every
 * dated card on the quote screen as unsourced: a warning that fires on
 * everything, which is the same as a warning that fires on nothing.
 */
export type RateSource = {
  effectiveOn?: Date | string | null;
  sourceNote?: string | null;
};

export function rateProvenance(row: RateSource): RateProvenance {
  // Whitespace is not a citation. A source note of one stray space was produced
  // by somebody tabbing through the form, which is precisely the case this is
  // here to catch rather than to wave through.
  if (typeof row.sourceNote === 'string' && row.sourceNote.trim() !== '') return 'documented';

  const on = row.effectiveOn;
  if (on instanceof Date) return Number.isNaN(on.getTime()) ? 'unsourced' : 'dated';
  if (typeof on === 'string' && on.trim() !== '') {
    return Number.isNaN(new Date(on).getTime()) ? 'unsourced' : 'dated';
  }
  return 'unsourced';
}

/** True when a premium from this row traces to no document and no date. */
export function isUnsourcedRate(row: RateSource): boolean {
  return rateProvenance(row) === 'unsourced';
}

/* ------------------------------------------------------------ eligibility -- */

export type Ineligibility =
  | { reason: 'age'; minAge: number; maxAge: number }
  | { reason: 'face'; minFaceCents: number; maxFaceCents: number }
  | { reason: 'state'; states: string[] }
  | { reason: 'tobacco' }
  | { reason: 'archived' };

export const INELIGIBLE_LABEL = (why: Ineligibility): string => {
  switch (why.reason) {
    case 'age':
      return `Issue ages ${why.minAge}–${why.maxAge}`;
    case 'face':
      return `Face ${money(why.minFaceCents)}–${money(why.maxFaceCents)}`;
    case 'state':
      return `Not filed in this state`;
    case 'tobacco':
      return 'Not written for tobacco users';
    case 'archived':
      return 'No longer written';
    default:
      return 'Cannot be written';
  }
};

const money = (cents: number): string => `$${Math.round(cents / 100).toLocaleString('en-US')}`;

/**
 * Can this product be written for this person?
 *
 * Returns null when it can, and the first reason it cannot otherwise. The
 * reason matters as much as the answer: an agent told "Ethos cannot write her"
 * learns nothing, and an agent told "Ethos caps at $200,000" knows to ask
 * whether $200,000 would do.
 */
export function whyNot(p: CarrierProduct, a: Applicant): Ineligibility | null {
  if (p.archivedAt) return { reason: 'archived' };
  if (a.age < p.minAge || a.age > p.maxAge) {
    return { reason: 'age', minAge: p.minAge, maxAge: p.maxAge };
  }
  if (a.faceCents !== null && (a.faceCents < p.minFaceCents || a.faceCents > p.maxFaceCents)) {
    return { reason: 'face', minFaceCents: p.minFaceCents, maxFaceCents: p.maxFaceCents };
  }
  // An empty state list means the product is not restricted, which is the
  // common case; a filled one is exhaustive.
  if (p.states.length > 0 && !p.states.includes(a.state.toUpperCase())) {
    return { reason: 'state', states: p.states };
  }
  if (a.tobacco && !p.tobaccoAllowed) return { reason: 'tobacco' };
  return null;
}

export const canWrite = (p: CarrierProduct, a: Applicant): boolean => whyNot(p, a) === null;

/* -------------------------------------------------------------- the price -- */

export interface Priced {
  productId: string;
  rateClass: string;
  termYears: number | null;
  /**
   * The face amount this premium buys. Always stated, because when no face was
   * asked for the screen shows several and a premium without its cover amount
   * beside it is a number nobody can use.
   */
  faceCents: number | null;
  /** Monthly premium in cents, from the card. */
  monthlyCents: number;
  annualCents: number;
  /** The row it came from, so the number can always be traced to a card. */
  source: RateRow;
}

/**
 * The premium for one applicant on one product, from rows the agency loaded.
 *
 * Returns every class the card offers for that age, cheapest first, so an agent
 * can see what preferred would save if the client qualifies for it. Returns an
 * empty list when no row matches — which is a real answer, not a failure, and
 * the screens say "rates not loaded" rather than inventing one.
 *
 * NOTHING IS INTERPOLATED
 * Not between ages, not between face amounts, not between term lengths. Rate
 * cards are step functions with underwriting behind every step, and a premium
 * halfway between two printed rows is a number no carrier will honour.
 */
export function price(
  p: CarrierProduct, a: Applicant, rows: readonly RateRow[], termYears?: number,
): Priced[] {
  if (!canWrite(p, a)) return [];

  const term = termYears ?? (p.termYears.length === 1 ? p.termYears[0]! : null);

  const matches = rows.filter((r) => r.productId === p.id
    && r.sex === a.sex
    && r.tobacco === a.tobacco
    && r.age === a.age
    && (term === null || r.termYears === null || r.termYears === term)
    // A 'table' product prices per face amount and must match it exactly. A
    // 'per_thousand' product's rate covers the whole band.
    // A 'table' product prices per face amount and must match it exactly —
    // unless none was asked for, in which case every printed face is an answer.
    && (p.rateMode === 'per_thousand'
      || a.faceCents === null
      || r.faceCents === a.faceCents));

  /*
   * A per-thousand product's premium is rate x face. With no face amount there
   * is nothing to multiply, and guessing one would produce a premium for cover
   * nobody asked for — so it returns nothing and the screen reports it as
   * unpriced, which is true.
   */
  if (p.rateMode === 'per_thousand' && a.faceCents === null) return [];

  const out: Priced[] = matches.map((r) => {
    const monthlyCents = p.rateMode === 'per_thousand'
      // Per thousand of cover, plus the flat policy fee. Rounded once, at the
      // end, to the cent — the way the carrier's own illustration does it.
      ? Math.round(((a.faceCents ?? 0) / 100_000) * r.rateCents) + p.policyFeeCents
      : r.rateCents;
    return {
      productId: p.id,
      rateClass: r.rateClass,
      termYears: r.termYears,
      faceCents: p.rateMode === 'per_thousand' ? a.faceCents : r.faceCents,
      monthlyCents,
      annualCents: annualisePremium(monthlyCents, 'monthly' as PremiumMode),
      source: r,
    };
  });

  return out.sort((x, y) => x.monthlyCents - y.monthlyCents);
}

/* ---------------------------------------------------------- what they need -- */

/**
 * How much cover a household actually needs.
 *
 * The DIME method — Debt, Income, Mortgage, Education — because it is what the
 * industry teaches, what an agent can explain across a kitchen table in a
 * minute, and arithmetic rather than opinion. Every figure comes from the
 * client's own answers, so the result is exactly as good as what they said and
 * no better.
 *
 * It is a starting figure for a conversation, not an underwriting decision.
 */
export interface NeedsInput {
  /** Debts other than the mortgage: cards, loans, medical. */
  debtsCents: number;
  /** Annual household income the family would lose. */
  annualIncomeCents: number;
  /** How many years to replace it for. Ten is the common rule of thumb. */
  incomeYears: number;
  mortgageCents: number;
  /** What it would cost to educate the children. */
  educationCents: number;
  /** Funeral and final expenses. */
  finalExpensesCents: number;
  /** Cover they already have, in force. */
  existingCoverCents: number;
  /** Savings and liquid assets the family could actually use. */
  liquidAssetsCents: number;
}

export interface Needs {
  /** Everything the family would have to cover. */
  totalNeedCents: number;
  /** What they already have against it. */
  offsetCents: number;
  /** The gap, before rounding. Never negative. */
  gapCents: number;
  /** The gap rounded to a face amount a carrier actually sells. */
  suggestedFaceCents: number;
  /** Each line, so an agent can show their working. */
  lines: Array<{ label: string; cents: number }>;
}

export function needsAnalysis(input: NeedsInput): Needs {
  const n = (v: number): number => (Number.isFinite(v) && v > 0 ? Math.round(v) : 0);

  const income = n(input.annualIncomeCents) * Math.max(0, Math.round(input.incomeYears || 0));
  const lines = [
    { label: 'Income to replace', cents: income },
    { label: 'Mortgage', cents: n(input.mortgageCents) },
    { label: 'Other debts', cents: n(input.debtsCents) },
    { label: 'Education', cents: n(input.educationCents) },
    { label: 'Final expenses', cents: n(input.finalExpensesCents) },
  ];
  const totalNeedCents = lines.reduce((a, l) => a + l.cents, 0);
  const offsetCents = n(input.existingCoverCents) + n(input.liquidAssetsCents);
  const gapCents = Math.max(0, totalNeedCents - offsetCents);

  return {
    totalNeedCents,
    offsetCents,
    gapCents,
    suggestedFaceCents: roundToSellableFace(gapCents),
    lines,
  };
}

/**
 * Rounds a gap up to a face amount somebody actually sells.
 *
 * Carriers sell round numbers. A need of $237,400 is quoted at $250,000, not at
 * $237,400, and an agent reading a precise figure off a screen and then asking
 * for a different one looks like the software got it wrong.
 *
 * Rounds UP, always: the number is what the family is short by, and rounding it
 * down to look tidier leaves them short on purpose.
 */
export function roundToSellableFace(cents: number): number {
  if (cents <= 0) return 0;
  const dollars = cents / 100;
  const step = dollars <= 50_000 ? 5_000
    : dollars <= 100_000 ? 10_000
      : dollars <= 500_000 ? 25_000
        : dollars <= 1_000_000 ? 50_000
          : 100_000;
  return Math.ceil(dollars / step) * step * 100;
}

/* ------------------------------------------------------------ the comparison -- */

export interface QuoteOption {
  product: CarrierProduct;
  carrierName: string;
  /** Empty when the agency has not loaded this product's rate card yet. */
  prices: Priced[];
  /** Set when the product cannot be written for this person at all. */
  ineligible: Ineligibility | null;
}

/**
 * Everything an agency could offer this person, priced where it can be.
 *
 * Eligible products come first, cheapest real premium first, then eligible
 * products with no rates loaded, then the ones that cannot be written. An agent
 * on a call reads down the list and stops at the first row — so the order is
 * the recommendation, and it has to be honest about which rows are prices and
 * which are only possibilities.
 */
export function compare(
  options: readonly QuoteOption[],
): { priced: QuoteOption[]; unpriced: QuoteOption[]; ineligible: QuoteOption[] } {
  const priced = options
    .filter((o) => !o.ineligible && o.prices.length > 0)
    .sort((a, b) => (a.prices[0]!.monthlyCents) - (b.prices[0]!.monthlyCents));
  const unpriced = options.filter((o) => !o.ineligible && o.prices.length === 0);
  const ineligible = options.filter((o) => o.ineligible !== null);
  return { priced, unpriced, ineligible };
}

/** Age at a date, from a date of birth. Whole years, the way a carrier counts. */
export function ageOn(dob: Date, on: Date): number {
  let age = on.getUTCFullYear() - dob.getUTCFullYear();
  const m = on.getUTCMonth() - dob.getUTCMonth();
  if (m < 0 || (m === 0 && on.getUTCDate() < dob.getUTCDate())) age -= 1;
  return age;
}
